import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'

import { IQuestionComponentProps, IQuestionComponentRef, QuestionViewFrame } from '../types'
import { SlidersQuestionModelTypes, SlidersResponse, ISlidersResponseValidation, ISlidersQuestionSchema } from './types'

import useSession from '../../session/useSession'
import usePreviewOptions from '@aytm/app/swidget/base/modules/preview/usePreviewOptions'

import SliderView from './View'
import QuestionFrame from '../_common/QuestionFrame/QuestionFrame'

import { getSmallAssetPath } from '@aytm/lib/common/assets'
import imageAssetWithOptions from '@aytm/app/swidget/base/modules/preview/functions/imageAssetWithOptions'
import VideoBlock from '../_common/VideoBlock/VideoBlock'

const SlidersQuestion = React.memo(
  React.forwardRef<IQuestionComponentRef, IQuestionComponentProps<SlidersQuestionModelTypes>>(({ model }, ref) => {
    const [[__response, responseValidation, __frame], setResponse] = useState<
      [SlidersResponse, ISlidersResponseValidation, QuestionViewFrame]
    >([model.response, model.responseValidation, model.frame])

    const session = useSession()
    const previewOptions = usePreviewOptions()
    const [showValidation, setShowValidation] = useState(false)
    const init = useRef(null)
    const [videoWatched, setVideoWatched] = useState(!model.schema.videos?.length)

    useEffect(() => {
      session.enableNext(responseValidation.valid && videoWatched)
    }, [session, responseValidation.valid, videoWatched])

    if (!init.current) {
      init.current = true
      model.response.data.forEach(item => {
        item.hidden = false == session.rules?.show?.[item.id]
      })
      model.response = { ...model.response }
      setResponse([model.response, model.responseValidation, model.frame])
    }

    const schema = useMemo(() => {
      const localSchema = JSON.parse(JSON.stringify(model.schema)) as ISlidersQuestionSchema
      localSchema.matrix_batches.forEach(a => {
        a.image = imageAssetWithOptions(session.rules?.sub?.[localSchema.id]?.[a.id], a.image)

        a.empty = a.hidden || false // se sr mx may have empty batches - when all batches deleted in editor backend add one with property hidden: true
        if (a.empty) a.text = ''

        a.fakeHidden =
          previewOptions.showHidden && false == session.rules?.show?.[a.id] && !previewOptions.suppressLogic
        a.hidden = !previewOptions.showHidden && false == session.rules?.show?.[a.id] // hidden by logic
        return a
      })

      return localSchema
    }, [model.schema, previewOptions, session.rules?.show, session.rules?.sub])

    const onUpdateResponse = (response: SlidersResponse) => {
      model.response = response
      setResponse([model.response, model.responseValidation, model.frame])
    }

    useImperativeHandle<IQuestionComponentRef, IQuestionComponentRef>(
      ref,
      () => {
        return {
          tryNext() {
            if (model.responseValidation.valid || session.skipValidation) {
              return false
            } else {
              session.enableNext(false)
              return true
            }
          },
          tryPrev() {
            return false
          },
          showValidation() {
            setShowValidation(true)
          }
        }
      },
      [model, session]
    )

    const imagesRef = useRef<string[]>(null)
    if (!imagesRef.current)
      imagesRef.current = model.schema.matrix_batches.reduce((array: string[], batch) => {
        if (model.schema.matrix_batches || (batch?.image && !batch.image.expand))
          array.push(getSmallAssetPath(batch.image))
        return array
      }, [])

    return (
      <QuestionFrame model={model} images={imagesRef.current}>
        <VideoBlock
          videos={model.schema.videos}
          media_mask={model.schema.media_mask}
          id={model.schema.id}
          showValidationError={showValidation}
          onWatched={() => setVideoWatched(true)}
        />
        <SliderView
          schema={schema}
          responseValidation={model.responseValidation}
          response={model.response}
          showValidation={showValidation}
          onUpdateResponse={onUpdateResponse}
        />
      </QuestionFrame>
    )
  }),
  (prevProps, nextProps) => prevProps.model === nextProps.model
)

SlidersQuestion.displayName = 'SlidersQuestion'

export default SlidersQuestion
